import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import _debounce from "lodash/debounce";

import KolulaStorageManager from "../../../../storage/KolulaStorageManager";
import { RootState } from "../../../../redux/store";
import {
  accountProfilePrefilled,
  personalDataDraftLoaded,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { PersonalData } from "../../../../types/bookingCreation";

const SAVE_DEBOUNCE_MS = 500;

/**
 * Persists the bookingCreation.personalData slice to IndexedDB so the form
 * survives a browser refresh. PII rule (apps/end-user/CLAUDE.md): never put
 * personalData fields in the URL — IndexedDB is the right store.
 *
 * Lifecycle:
 *  - The draft is read from IDB ONCE per page load (`personalDataDraftRestored`
 *    in the slice), not on every mount. The step remounts on every entry, and
 *    a re-read there overwrote the newer Redux values with a draft the
 *    debounced save had not caught up with.
 *  - The save effect is gated on the draft having been read, so the initial
 *    empty Redux state never overwrites an existing IDB draft when the load is
 *    slow (>500ms — happens on cold mobile devices).
 *  - On every personalData change after that: debounced save to IDB.
 *  - Clearing the draft is the caller's responsibility — call
 *    `KolulaStorageManager.deletePersonalData()` on bookingCreationCompleted.
 *  - H-3a F2: after the draft is read, a logged-in account's profile data
 *    (name, phone, email — the non-empty fields) overrides it, so account data
 *    wins over stale guest drafts. This happens once per account and draft
 *    (`accountPrefillAppliedFor`, saved with the draft): repeating it on every
 *    step entry or refresh overwrote the customer's own edits. `isAgreed` is
 *    never touched.
 */
export function usePersonalDataPersistence() {
  const dispatch = useDispatch();
  const personalData = useSelector(
    (state: RootState) => state.bookingCreation.personalData
  );
  const draftRestored = useSelector(
    (state: RootState) => state.bookingCreation.personalDataDraftRestored
  );
  const prefillAppliedFor = useSelector(
    (state: RootState) => state.bookingCreation.accountPrefillAppliedFor
  );
  const accountProfile = useSelector(
    (state: RootState) => state.account?.profile
  );
  const storeRef = useRef<KolulaStorageManager | null>(null);
  const loadStartedRef = useRef(false);

  if (!storeRef.current) {
    storeRef.current = new KolulaStorageManager();
  }

  useEffect(() => {
    if (draftRestored || loadStartedRef.current) return;
    loadStartedRef.current = true;
    const store = storeRef.current;
    if (!store) {
      dispatch(
        personalDataDraftLoaded({ data: null, accountPrefillAppliedFor: null })
      );
      return;
    }
    let data: Partial<PersonalData> | null = null;
    let appliedFor: string | null = null;
    store
      .getPersonalData()
      .then((stored) => {
        if (stored) {
          // Strip the IDB key and `isAgreed` — consent must be re-collected
          // on every booking, never auto-restored from a prior session.
          const {
            id: _id,
            isAgreed: _ia,
            accountPrefillAppliedFor,
            ...rest
          } = stored;
          data = rest;
          appliedFor = accountPrefillAppliedFor ?? null;
        }
      })
      .catch(() => {
        // IDB unavailable — proceed without hydration; not user-visible.
      })
      .finally(() =>
        dispatch(
          personalDataDraftLoaded({ data, accountPrefillAppliedFor: appliedFor })
        )
      );
  }, [draftRestored, dispatch]);

  // Logged-in prefill — strictly AFTER the draft was read, so the account
  // profile wins over the stored draft; and only for an account it has not
  // been applied for yet. Only non-empty profile fields are applied;
  // `isAgreed` stays untouched (consent is re-collected on every booking).
  useEffect(() => {
    if (!draftRestored || !accountProfile) return;
    if (prefillAppliedFor === accountProfile.id) return;
    const prefill: Partial<PersonalData> = {};
    if (accountProfile.firstName) prefill.firstName = accountProfile.firstName;
    if (accountProfile.lastName) prefill.lastName = accountProfile.lastName;
    if (accountProfile.phone) prefill.phone = accountProfile.phone;
    if (accountProfile.email) prefill.email = accountProfile.email;
    dispatch(accountProfilePrefilled({ accountId: accountProfile.id, prefill }));
  }, [draftRestored, prefillAppliedFor, accountProfile, dispatch]);

  useEffect(() => {
    if (!draftRestored) return;
    const store = storeRef.current;
    if (!store) return;
    const save = _debounce((data: PersonalData) => {
      // Never persist consent across sessions.
      const { isAgreed: _ia, ...persistable } = data;
      store
        .setPersonalData({
          ...persistable,
          isAgreed: false,
          accountPrefillAppliedFor: prefillAppliedFor,
          id: "personal_data",
        })
        .catch(() => {
          // IDB write failed — silent; next change will retry.
        });
    }, SAVE_DEBOUNCE_MS);
    save(personalData);
    return () => {
      save.cancel();
    };
  }, [personalData, prefillAppliedFor, draftRestored]);
}
